/**
 * ESTABLISHMENT-IMPORT-TASKS
 * Taches de fond de l'etablissement (scan), affichees par PendingTasks.
 * L'etat vient du back (options du compte), redemande a intervalle regulier tant qu'une tache tourne.
 * Un scan termine ouvre automatiquement son panneau global.
 */
import PendingTasks from "@/components/PendingTasks"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { PendingTask } from "@/redux/account.types"
import { useEffect, useRef } from "react"
import establishmentStore from "../model/establishment.store"
import EstablishmentScanPanel from "./EstablishmentScanPanel"
import { sessionEdit } from "@/redux/_session.actions"

const POLLING_DELAY_MS = 4000

interface Props {
  onPanelWidthChange: (width: number) => void
}

export default function EstablishmentImportTasks({ onPanelWidthChange }: Props) {
  const dispatch = useAppDispatch()
  const establishmentId = useAppSelector((state) => state._session.establishmentId)
  const pendingTasks = useAppSelector((state) => state._session.accountOptions.pendingTasks)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const openingScanTasks = useRef(new Set<string>())
  const scanPanelEstablishmentId = useAppSelector((state) => state._session.scanPanelEstablishmentId)

  const tasks = (pendingTasks ?? []).filter((task) => task.EstablishmentId === establishmentId)
  const isRunning = tasks.some((task) => task.status === "pending")

  useEffect(() => {
    if (!isRunning) return
    const interval = setInterval(() => dispatch(establishmentStore.thunks.fetchImportTasks()), POLLING_DELAY_MS)
    return () => clearInterval(interval)
  }, [isRunning])

  useEffect(() => {
    for (const task of tasks) {
      if (task.type !== "establishment-scan" || task.status !== "done" || openingScanTasks.current.has(task.id))
        continue
      openingScanTasks.current.add(task.id)
      openTask(task)
    }
  }, [pendingTasks, establishmentId])

  async function openTask(task: PendingTask) {
    //Ancienne tache d'import (correction d'orthographe) : plus rien a ouvrir
    if (task.type !== "establishment-scan") return removeTask(task.id)
    //Un scan persiste peut etre redemande
    const response: any = await dispatch(establishmentStore.thunks.fetchImportTaskResult(task.id))
    if (response.payload?.error || !Array.isArray(response.payload)) return
    dispatch(sessionEdit("scanPanelEstablishmentId", task.EstablishmentId))
    removeTask(task.id)
  }

  function removeTask(taskId: string) {
    dispatch(establishmentStore.thunks.removeImportTask(taskId))
  }

  return (
    <>
      <PendingTasks
        tasks={tasks}
        doneText="Traitement terminé."
        onContinue={(task) => openTask(task as PendingTask)}
        onRemove={(task) => removeTask(task.id)}
      />

      {scanPanelEstablishmentId && scanPanelEstablishmentId === establishmentId && (
        <EstablishmentScanPanel
          items={establishment.scanResults ?? []}
          onWidthChange={onPanelWidthChange}
          onClose={() => dispatch(sessionEdit("scanPanelEstablishmentId", null))}
        />
      )}
    </>
  )
}
